import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import changeLogService from "services/change-log-service";
import crudsService from "services/cruds-service";
import formatPartCode from "helper/formatPartCode";
import { formatIdentifier } from "cruds/Inventory-management/components/helperFunctions";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

import DataTable from "e4acomponents/Tables/DataTable";
import ImageModal from "e4acomponents/ImageModal";

export default function ChangeLogsTable({
    documentType,
}) {

    const [changes, setChanges] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [avatars, setAvatars] = useState([]);

    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [openImageModal, setOpenImageModal] = useState(false);
    const [imageUrl, setImageUrl] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                // Fetch recent changes from the server
                const changesDataResponse = await changeLogService.getTypeChanges(documentType);
                const changesData = changesDataResponse.data.attributes;

                const images = await fetchImages(changesData);

                setChanges(changesData);
                setAvatars(images);
            } catch (error) {
                console.error("Error fetching recent changes:", error);
            }
        })();
    }, [documentType]);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        setTableData(getRows(changes));
    }, [avatars, changes, documentType]);

    useEffect(() => {
        if (tableData.length > 0) {
            setIsLoading(false);
        }
    }, [tableData]);

    // Fetches the user's profile images from the server
    const fetchImages = async (changeLogs) => {
        let images = {};
        for (let i = 0; i < changeLogs.length; i++) {
            const change = changeLogs[i]
            if (change.user && !images[change.user._id] && !avatars[change.user._id]) {
                try {
                    images[change.user._id] = await crudsService.getImage(change.user.profile_image);
                } catch (err) {
                    images[change.user._id] = change.user.profile_image;
                }
            }
        }
        return images;
    }

    // Transforms the date to time passed since the modification's date
    // e.g. 2 days ago, 3 weeks ago, etc.
    const formatDate = (date) => {
        const now = new Date();

        const changeDate = new Date(date);
        const diffMs = now - changeDate;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);

        if (diffMs < 60000) return `${Math.floor(diffMs / 1000)} seconds ago`;
        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffMonths < 1) return `${diffWeeks} weeks ago`;
        return `${diffMonths} months ago`;
    }

    // Generate dataTable rows from the changeLog data
    const getRows = (info) => {
        return info && info.length ? info.map((item) => {
            const isDeleted = item.isDeleted;
            let identifier = "N/A";
            let route = null;
            let isClickable = false;

            switch (documentType) {
                case 'parts':
                    identifier = item.partCode ? formatPartCode(item.partCode) : "N/A";
                    if (!isDeleted && item.partCode) {
                        route = `/part-code-management/view-part-code/${item.partCode._id}`;
                        isClickable = true;
                    }
                    break;
                case 'inventoryitems':
                    identifier = item.inventoryItem ? formatIdentifier(item.inventoryItem) : "N/A";
                    if (!isDeleted && item.inventoryItem) {
                        route = `/inventory-management/view-specific-item/${item.inventoryItem._id}`;
                        isClickable = true;
                    }
                    break;
                case 'partcategories':
                    identifier = item.partCategory ? item.partCategory.categoryCode : "N/A";
                    isClickable = false;
                    break;
                case 'products':
                    identifier = item.product ? item.product.serialNumber : "N/A";
                    if (!isDeleted && item.product) {
                        route = `/fulfillment-center/view-product/${item.product._id}`;
                        isClickable = true;
                    }
                    break;
                case 'orders':
                    identifier = item.order ? item.order.order_num : "N/A";
                    isClickable = false;
                    break;
                default:
                    identifier = "N/A";
            }

            if (isDeleted &&
                (item.documentType === 'parts' || item.documentType === 'inventoryitems')) {
                identifier = formatPartCode(item.identifier) || identifier;
            } else if (isDeleted) {
                identifier = item.identifier || identifier;
            }

            return {
                identifier: isClickable ? (
                    <a onClick={() => navigate(route)} style={{ cursor: 'pointer', color: '#344767' }}>
                        {identifier}
                    </a>) : identifier,
                changeType: item.type,
                date: formatDate(item.created_at),
                username: item.user ? item.user.name : "Unknown",
                avatar: item.user ? avatars[item.user._id] : null,
                id: item._id
            };
        }) : [];
    }

    const getIdentifierHeader = () => {
        switch (documentType) {
            case 'partcategories': return "Category Code";
            case 'products': return "Serial Number";
            case 'orders': return "Order Number";
            default: return "Part Code";
        }
    }

    const handleOpenImageModal = (url) => {
        setImageUrl(url);
        setOpenImageModal(true);
    }

    // DataTable columns configuration
    const dataTableColumns = {
        columns: [
            {
                Header: "Avatar", accessor: "avatar", width: "15%", disableSortBy: true, Cell: ({ cell: { value } }) => {
                    return (
                        <MDBox
                            onClick={() => handleOpenImageModal(value)}
                            sx={{ cursor: "pointer" }}
                        >
                            <MDAvatar src={value} alt="profile-image" size="xs" shadow="sm" />
                        </MDBox>

                    )
                }
            },
            { Header: "Username", accessor: "username", width: "20%" },
            { Header: getIdentifierHeader(), accessor: "identifier", width: "25%" },
            { Header: "Change Type", accessor: "changeType", width: "20%" },
            { Header: "Date", accessor: "date", width: "20%" },
        ],
    }

    const dataTableTransition = {
        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: avatars ? 1 : 0,
        visibility: avatars ? 'visible' : 'hidden',
        maxHeight: avatars ? '2000px' : '0px',
        marginTop: avatars ? '16px' : '0px',
        overflow: 'hidden',
        '& .MuiTableRow-root': {
            opacity: 0,
            animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
            animationDelay: 'calc(var(--row-index) * 50ms)'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0,
                transform: 'translateY(10px)'
            },
            '100%': {
                opacity: 1,
                transform: 'translateY(0)'
            }
        }
    }

    return (
        <Card sx={{ width: "100%", overflow: "visible", height: "100%" }}>

            {/* Header: Icon and title */}
            <MDBox display="flex">
                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    variant="gradient"
                    bgColor="warning"
                    color="white"
                    shadow="md"
                    borderRadius="xl"
                    ml={3}
                    mt={-2}
                >
                    <Icon fontSize="medium" color="inherit">
                        published_with_changes
                    </Icon>
                </MDBox>
                <MDTypography variant="h5" sx={{ mt: 2, mb: 1, ml: 2 }}>
                    Recent Changes
                </MDTypography>

            </MDBox>

            <MDBox p={2} sx={{ overflow: "hidden", maxWidth: "100%" }}>

                {isLoading ? (
                    <MDBox
                        p={5}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        height="100%"
                    >
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <>
                        <ImageModal
                            mediaURL={imageUrl}
                            open={openImageModal}
                            onClose={() => setOpenImageModal(false)}
                        />

                        <MDBox sx={dataTableTransition}>
                            <DataTable
                                table={{ ...dataTableColumns, rows: tableData }}
                                entriesPerPage={{ show: false, entries: [10], defaultValue: 10 }}
                                showTotalEntries
                                showEntriesPerPage
                                canSearch
                                tableId="change-logs-table"
                            />
                        </MDBox>
                    </>
                )}

            </MDBox>

        </Card>
    )
}